import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import BookCard from "@/components/BookCard";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { BOOKS } from "@/data/books";

export default function Collection() {
  return (
    <section data-testid="collection-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
          <Reveal>
            <Eyebrow className="mb-4">The Collection</Eyebrow>
            <div className="flex items-center gap-5">
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-walnut">
                Five adventures.
                <br />
                One mission.
              </h2>
              <motion.div
                aria-hidden="true"
                data-testid="collection-rocket"
                className="pointer-events-none shrink-0"
                initial={{ y: 70, opacity: 0, rotate: -12 }}
                whileInView={{ y: 0, opacity: 1, rotate: 6 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ type: "spring", stiffness: 46, damping: 9, delay: 0.3 }}
              >
                <div className="animate-float-slow">
                  <svg width="52" height="90" viewBox="0 0 86 150">
                    <g className="flame-flicker" style={{ transformOrigin: "43px 118px" }}>
                      <path d="M43 118 C 51 132 49 142 43 150 C 37 142 35 132 43 118 Z" fill="#FFE9A8" />
                      <path d="M43 122 C 48 131 47 138 43 144 C 39 138 38 131 43 122 Z" fill="#F5A623" />
                    </g>
                    <path d="M22 96 L8 122 L26 114 Z" fill="#71BE6B" stroke="#31435C" strokeWidth="3" strokeLinejoin="round" />
                    <path d="M64 96 L78 122 L60 114 Z" fill="#71BE6B" stroke="#31435C" strokeWidth="3" strokeLinejoin="round" />
                    <path d="M43 8 C 60 26 64 52 64 78 L64 108 C 64 116 58 122 43 122 C 28 122 22 116 22 108 L22 78 C 22 52 26 26 43 8 Z" fill="#FFFDF2" stroke="#31435C" strokeWidth="3.5" />
                    <path d="M43 8 C 53 18 59 30 61 44 L25 44 C 27 30 33 18 43 8 Z" fill="#4FA3E3" stroke="#31435C" strokeWidth="3.5" />
                    <circle cx="43" cy="72" r="13" fill="#BFE0F7" stroke="#31435C" strokeWidth="3.5" />
                    <circle cx="39" cy="68" r="4" fill="#FFFDF2" opacity="0.8" />
                    <rect x="34" y="94" width="18" height="8" rx="4" fill="#71BE6B" stroke="#31435C" strokeWidth="2.5" />
                  </svg>
                </div>
              </motion.div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-sm text-sm sm:text-base text-walnutsoft leading-relaxed">
              Each Bailey story is written by Damali Allison and illustrated by Meni Etim —
              designed to build confidence, celebrate diversity, and spark a love of reading.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {BOOKS.map((book, i) => (
            <BookCard key={book.slug} book={book} delay={(i % 3) * 0.12} />
          ))}
          <Reveal delay={0.24} className="flex">
            <Link
              to="/books"
              data-testid="collection-view-all-link"
              className="group flex flex-col justify-between w-full min-h-[16rem] rounded-[1.75rem] bg-walnut text-cream p-7 hover:bg-terracotta transition-colors duration-500"
            >
              <span className="font-mono text-sm opacity-60">→</span>
              <div>
                <p className="font-display font-bold text-2xl sm:text-3xl tracking-tight leading-tight mb-3">
                  Explore the whole series
                </p>
                <span className="inline-flex items-center gap-2 text-sm font-semibold opacity-90 group-hover:gap-3 transition-all">
                  View all books <ArrowRight size={16} />
                </span>
              </div>
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
